import { getAnalyticsPluginInfoById, sceneChangeDetectionPlugin } from '@mimir/common/dto/analyticsModels';
import { type DynamoItemType, ItemType } from '@mimir/common/dto/itemTypes';
import type { AnalyticConfig } from '@mimir/common/dto/tenantModel';
import { canPerformAnalyticsOperation } from '@mimir/common/util/security';
import cloneDeep from 'lodash/cloneDeep';

/**
 * Filters analytics config based on user permissions.
 * Disables analytics that the user does not have permission to use.
 * This is used by UI components to show the correct analytics count before AnalyticOptions normalizes.
 */
export function filterAnalyticConfigByUserPermissions(
  config: AnalyticConfig,
  userPermissions: string[]
): AnalyticConfig {
  const filtered = cloneDeep(config);

  if (!canPerformAnalyticsOperation(userPermissions, 'transcription')) {
    filtered.transcriptionEnabled = false;
    filtered.transcriptionConfig = undefined;
  }

  if (!canPerformAnalyticsOperation(userPermissions, 'personDetection')) {
    filtered.personDetectionEnabled = false;
    filtered.personDetectionConfig = undefined;
  }

  if (!canPerformAnalyticsOperation(userPermissions, 'celebrityDetection')) {
    filtered.celebrityDetectionEnabled = false;
  }

  if (!canPerformAnalyticsOperation(userPermissions, 'labelDetection')) {
    filtered.labelDetectionEnabled = false;
  }

  if (!canPerformAnalyticsOperation(userPermissions, 'textDetection')) {
    filtered.textDetectionEnabled = false;
    filtered.textDetectionSettings = undefined;
  }

  if (!canPerformAnalyticsOperation(userPermissions, 'sceneDetection')) {
    const analyticPlugins = filtered.analyticPlugins;
    if (analyticPlugins && analyticPlugins[sceneChangeDetectionPlugin.id]) {
      analyticPlugins[sceneChangeDetectionPlugin.id].enabled = false;
    }
  }

  return filtered;
}

export const getAnalyticConfig = (itemType: DynamoItemType, analyticConfig: AnalyticConfig): AnalyticConfig => {
  const configCopy = cloneDeep(analyticConfig);

  const analyticPlugins = configCopy.analyticPlugins;
  if (analyticPlugins) {
    for (const key in analyticPlugins) {
      const plugin = analyticPlugins[key];
      if (!plugin.enabled) {
        continue;
      }
      const pluginInfo = getAnalyticsPluginInfoById(plugin.id);
      if (!pluginInfo || !(pluginInfo.compatibleItemTypes ?? []).includes(itemType)) {
        plugin.enabled = false;
      }
    }
  }

  if (itemType === ItemType.AUDIO) {
    return {
      transcriptionEnabled: configCopy.transcriptionEnabled,
      transcriptionConfig: configCopy.transcriptionConfig,
      labelDetectionEnabled: false,
      textDetectionEnabled: false,
      personDetectionEnabled: false,
      celebrityDetectionEnabled: false,
      personDetectionConfig: undefined,
      textDetectionSettings: undefined,
      analyticPlugins,
    };
  } else if (itemType === ItemType.IMAGE) {
    return {
      ...configCopy,
      transcriptionEnabled: false,
      transcriptionConfig: undefined,
    };
  }

  return configCopy;
};
